<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import { useClientSettingsLabels } from 'custom/dashboard/composables/zimobi/useClientSettingsLabels';
import { isFeaturePending } from 'custom/dashboard/helper/zimobi/clientSettingsHelper';

const props = defineProps({
  // Entries from the backend `schema` payload: { key, group, config }.
  schema: {
    type: Array,
    default: () => [],
  },
  // Current toggle states, so the dialog only renders what is switched on.
  features: {
    type: Object,
    default: () => ({}),
  },
  modelValue: {
    type: Object,
    default: () => ({}),
  },
  // When set, narrows the dialog to this one feature — the flag the admin just switched
  // on. Null shows every enabled feature that takes configuration.
  focusKey: {
    type: String,
    default: null,
  },
  isUpdating: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['confirm']);

const { t } = useI18n();
const { featureLabel, fieldLabel, fieldExample } = useClientSettingsLabels();

const dialogRef = ref(null);
// Edited in isolation so closing the dialog discards the changes. It holds every
// section's fields at once, so switching sections never loses what was typed.
const draft = ref({});
const activeKey = ref(null);

const sections = computed(() =>
  props.schema.filter(
    feature =>
      props.features[feature.key] &&
      feature.config.length &&
      (!props.focusKey || feature.key === props.focusKey)
  )
);

// With a dozen integrations switched on, one long column has no sense of place; a side
// list turns it into pick-one-then-edit.
const showNav = computed(() => sections.value.length > 1);

const activeSection = computed(
  () =>
    sections.value.find(feature => feature.key === activeKey.value) ||
    sections.value[0]
);

const isPending = feature => isFeaturePending(feature, draft.value);

// A stored secret never reaches the browser, so the placeholder is the only way the admin
// can tell "already set, leave blank to keep" from "never configured".
const fieldPlaceholder = field => {
  if (!field.secret) return '';
  return field.configured
    ? t('CLIENT_SETTINGS.DIALOG.SECRET_STORED')
    : t('CLIENT_SETTINGS.DIALOG.SECRET_EMPTY');
};

const blankSecrets = () =>
  Object.fromEntries(
    sections.value.flatMap(feature =>
      feature.config.filter(field => field.secret).map(field => [field.key, ''])
    )
  );

const open = () => {
  draft.value = { ...props.modelValue, ...blankSecrets() };
  activeKey.value =
    props.focusKey ||
    sections.value.find(isPending)?.key ||
    sections.value[0]?.key ||
    null;
  dialogRef.value?.open();
};

const close = () => dialogRef.value?.close();

const onConfirm = () => emit('confirm', { ...draft.value });

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :width="showNav ? '3xl' : '2xl'"
    overflow-y-auto
    :title="$t('CLIENT_SETTINGS.DIALOG.TITLE')"
    :description="$t('CLIENT_SETTINGS.DIALOG.DESCRIPTION')"
    :confirm-button-label="$t('CLIENT_SETTINGS.SAVE')"
    :is-loading="isUpdating"
    @confirm="onConfirm"
  >
    <p
      v-if="!sections.length"
      class="text-sm text-n-slate-11"
      data-testid="configuration-empty"
    >
      {{ $t('CLIENT_SETTINGS.DIALOG.EMPTY') }}
    </p>

    <div v-else class="flex gap-6">
      <nav
        v-if="showNav"
        class="flex flex-col w-56 gap-1 pr-4 overflow-y-auto border-r shrink-0 max-h-96 border-n-weak"
        data-testid="configuration-nav"
      >
        <!-- type="button" matters: Dialog wraps the content in a form whose submit
             confirms, so a bare button here would save instead of switching section. -->
        <button
          v-for="feature in sections"
          :key="feature.key"
          type="button"
          class="flex items-center justify-between gap-2 px-3 py-2 text-sm text-left rounded-lg"
          :class="
            feature.key === activeSection.key
              ? 'bg-n-alpha-2 text-n-slate-12'
              : 'text-n-slate-11 hover:bg-n-alpha-1'
          "
          :aria-current="feature.key === activeSection.key"
          :data-testid="`configuration-nav-${feature.key}`"
          @click="activeKey = feature.key"
        >
          <span class="truncate">{{ featureLabel(feature.key) }}</span>
          <span
            v-if="isPending(feature)"
            class="w-2 h-2 rounded-full shrink-0 bg-n-amber-9"
            :title="$t('CLIENT_SETTINGS.NEEDS_CONFIGURATION')"
            :data-testid="`configuration-pending-${feature.key}`"
          />
        </button>
      </nav>

      <div
        class="flex flex-col flex-1 min-w-0 gap-3"
        :data-testid="`configuration-section-${activeSection.key}`"
      >
        <h4 class="text-sm font-medium text-n-slate-12">
          {{ featureLabel(activeSection.key) }}
        </h4>

        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <template v-for="field in activeSection.config" :key="field.key">
            <TextArea
              v-if="field.type === 'json'"
              :id="field.key"
              v-model="draft[field.key]"
              :label="fieldLabel(field.key)"
              :placeholder="fieldExample(field.key)"
              auto-height
              min-height="6rem"
              custom-text-area-class="font-mono"
              class="sm:col-span-2"
              :data-testid="`config-${field.key}`"
            />
            <Input
              v-else
              :id="field.key"
              v-model="draft[field.key]"
              :type="field.secret ? 'password' : 'text'"
              :label="fieldLabel(field.key)"
              :placeholder="fieldPlaceholder(field)"
              :data-testid="`config-${field.key}`"
            />
          </template>
        </div>
      </div>
    </div>
  </Dialog>
</template>
